import TrustBadge from "@/components/TrustBadge";
import { useMaticCarePrice } from "@/components/Cart/hooks";
import { Skeleton } from "@/components/ui/skeleton";
import useLayoutStore from "@/stores/useLayoutStore";
import { formatPrice } from "@/utils/ShopifyHelpers";
import { Cross } from "lucide-react";
import Link from "next/link";
import { useTrack } from "@/analytics/events";
import {
  MATIC_CARE_ADD_ON_HASH,
  MATIC_CARE_ADD_ON_OPEN_EVENT,
} from "@/consts/maticCare";

type MaticCareUpsellProps = {
  onAddMaticCare: () => Promise<void> | void;
  onInteractionStart?: ((target: HTMLButtonElement) => void) | undefined;
  isMutating?: boolean;
  quantity?: number;
};

export default function MaticCareUpsell({
  onAddMaticCare,
  onInteractionStart,
  isMutating = false,
  quantity = 1,
}: MaticCareUpsellProps) {
  const closeOverlays = useLayoutStore((state) => state.closeOverlays);
  const track = useTrack();
  const { unitPrice, currencyCode, isLoading } = useMaticCarePrice();

  const displayMaticCarePrice =
    unitPrice !== undefined && currencyCode
      ? formatPrice(unitPrice * quantity, currencyCode)
      : null;
  const addButtonLabel = displayMaticCarePrice
    ? `Add Matic Care for ${displayMaticCarePrice}`
    : "Add Matic Care";
  const handleLearnMoreClick = () => {
    // This closes the cart mid-purchase, so the exit needs to be visible.
    track("upsell_learn_more_clicked", { upsell: "matic-care" });
    closeOverlays();

    window.dispatchEvent(new Event(MATIC_CARE_ADD_ON_OPEN_EVENT));
  };

  return (
    <div className="mt-4 flex w-full flex-col items-end">
      <div className="grid w-full grid-cols-[1fr_auto] gap-x-[16px] gap-y-[16px] rounded-[8px] border border-[#dbdbdb] p-[16px] [@media(min-width:475px)]:grid-cols-[1fr_auto_auto] [@media(min-width:475px)]:gap-x-[24px]">
        <TrustBadge
          className="col-start-1 row-start-1 h-[36px] w-fit gap-[12px] rounded-[8px] bg-[#f5f3ef] px-[16px] py-[8px]"
          icon={
            <Cross
              className="size-[16px] text-[#FF4E4E]"
              fill="#FF7C7C80"
              strokeWidth={1.75}
            />
          }
          label={
            <span className="font-trimMono text-[14px] uppercase leading-[1.6] tracking-[0.42px] text-[#1f2022]">
              Matic Care
            </span>
          }
        />
        <span className="col-start-2 row-start-1 self-center font-trimMono text-[14px] uppercase leading-[1.6] tracking-[0.42px] text-[#1f2022]">
          {displayMaticCarePrice ? (
            `+ ${displayMaticCarePrice}`
          ) : isLoading ? (
            <Skeleton className="h-[18px] w-[64px] rounded-[4px] bg-[#dbdbdb]" />
          ) : null}
        </span>
        <button
          type="button"
          aria-label={addButtonLabel}
          onClick={(event) => {
            onInteractionStart?.(event.currentTarget);
            void onAddMaticCare();
          }}
          disabled={isMutating}
          className="col-start-2 row-start-4 flex h-[36px] w-[79px] shrink-0 items-center justify-center self-end rounded-[50px] border border-black bg-[#1f2022] px-0 font-trimMono text-[16px] uppercase leading-[1.6] tracking-[0.48px] text-white disabled:cursor-not-allowed disabled:opacity-50 [@media(min-width:475px)]:col-start-3 [@media(min-width:475px)]:row-start-1 [@media(min-width:475px)]:self-center"
        >
          Add
        </button>
        <div className="col-span-2 row-start-2 h-px w-full bg-[#dbdbdb] [@media(min-width:475px)]:col-span-3" />
        <p className="col-span-2 row-start-3 font-ppMori text-[14px] leading-[1.6] tracking-[-0.28px] text-[#54524e] [@media(min-width:475px)]:hidden">
          <span className="block whitespace-nowrap">
            Protect your Matic from the unexpected! Matic
          </span>
          <span className="block whitespace-nowrap">
            Care extends Matic&apos;s standard warranty to 2
          </span>
        </p>
        <p className="col-start-1 row-start-4 -mt-[16px] font-ppMori text-[14px] leading-[1.6] tracking-[-0.28px] text-[#54524e] [@media(min-width:475px)]:mt-0 [@media(min-width:475px)]:hidden">
          <span className="block whitespace-nowrap">
            years from purchase and adds
          </span>
          <span className="block whitespace-nowrap">
            accidental damage coverage.
          </span>
          <Link
            href={`/product${MATIC_CARE_ADD_ON_HASH}`}
            aria-label="Learn more about Matic Care"
            className="whitespace-nowrap underline"
            onClick={handleLearnMoreClick}
          >
            Learn more.
          </Link>
        </p>
        <p className="col-span-3 row-start-3 hidden w-full font-ppMori text-[14px] leading-[1.6] tracking-[-0.28px] text-[#54524e] [@media(min-width:475px)]:block">
          Protect your Matic from the unexpected! Matic Care extends
          Matic&apos;s standard warranty to 2 years from purchase and adds
          accidental damage coverage.{" "}
          <Link
            href={`/product${MATIC_CARE_ADD_ON_HASH}`}
            aria-label="Learn more about Matic Care"
            className="underline"
            onClick={handleLearnMoreClick}
          >
            Learn more.
          </Link>
        </p>
      </div>
    </div>
  );
}
